import { type JSX, useEffect, useRef, useState } from 'react';

import { LauncherModal } from './LauncherModal.js';

export interface SpeedTestModalProps {
  onClose: () => void;
  /** When set, footer shows a "← Back" link that pops one modal off
   *  the navigation stack instead of dismissing entirely. */
  onBack?: () => void;
}

/* ───── Test parameters ─────────────────────────────────────────────────
 * Hits our own licensing-server endpoints so the test goes against
 * the same infrastructure the launcher fetches its config from. No
 * third-party CORS surprises, no privacy-blocker collisions with
 * known speed-test domains (some corporate firewalls / extensions
 * outright block `speed.cloudflare.com` to prevent employee tests).
 *
 * The endpoints are CORS-wildcard, no auth, no PII — see the
 * licensing-server speed-test handler for the safety caps.
 */
const API_BASE =
  (import.meta.env['VITE_LICENSING_API_URL'] as string | undefined) ??
  'https://api.mobilauncher.com';

/* Byte source. We prefer Cloudflare's public speed-test backend
 * (speed.cloudflare.com) because it's CDN-backed and serves at line
 * rate — our own Worker generates bytes on demand and tops out far
 * below a fast link. If Cloudflare's host is unreachable (some
 * corporate firewalls block known speed-test domains), we fall back to
 * our edge Worker so the tool still works behind a lockdown. */
const CF_BASE = 'https://speed.cloudflare.com';
/* CF __down is bounded by ?bytes=N (no unbounded mode), so each download
 * stream re-requests this chunk in a loop until the phase timer fires. */
const CF_DOWN_CHUNK = 50_000_000;
const CF_PROBE_TIMEOUT_MS = 2_500;

interface SpeedEndpoints {
  source: 'cloudflare' | 'edge';
  /** Fresh URL (cache-busted) for a latency probe / empty download. */
  pingUrl: () => string;
  /** Fresh URL (cache-busted) for one download stream's payload. */
  downloadUrl: () => string;
  uploadUrl: string;
}

const rand = (): string => `${String(performance.now())}-${String(Math.random())}`;

const CLOUDFLARE_ENDPOINTS: SpeedEndpoints = {
  source: 'cloudflare',
  pingUrl: () => `${CF_BASE}/__down?bytes=0&r=${rand()}`,
  downloadUrl: () => `${CF_BASE}/__down?bytes=${String(CF_DOWN_CHUNK)}&r=${rand()}`,
  uploadUrl: `${CF_BASE}/__up`,
};

const EDGE_ENDPOINTS: SpeedEndpoints = {
  source: 'edge',
  pingUrl: () => `${API_BASE}/speedtest/down?bytes=0&cacheBust=${rand()}`,
  downloadUrl: () => `${API_BASE}/speedtest/down?stream=1&cacheBust=${rand()}`,
  uploadUrl: `${API_BASE}/speedtest/up`,
};

const PING_SAMPLES = 4;            // Ping samples — discard the first as warm-up.

/* Parallel-stream, time-boxed throughput test.
 *
 * A single TCP stream can't fill a fast link — its ceiling is the
 * congestion window ÷ round-trip time — and a small fixed payload
 * measures TCP slow-start rather than steady-state bandwidth. Both make
 * a naive test read far below fast.com / Ookla, which open many parallel
 * connections and measure a longer window. So we open N concurrent
 * streams, run for a fixed window, and only count throughput AFTER a
 * warm-up period so the ramp is excluded. */
const DOWNLOAD_STREAMS = 6;
const UPLOAD_STREAMS = 4;
const MEASURE_MS = 8_000;          // measurement window per phase
const WARMUP_MS = 1_500;           // discarded ramp at the start of each phase
const SAMPLE_MS = 100;             // gauge / chart update cadence
const UPLOAD_CHUNK_BYTES = 1_000_000;
/* Safety guard: stop a phase early if it pulls this many bytes, so a
 * gigabit link can't move >1GB before the timer ends (protects metered
 * / cellular kiosks). Steady-state math still holds when we stop early —
 * we divide post-warm-up bytes by post-warm-up time. */
const MAX_PHASE_BYTES = 750_000_000;

type Phase =
  | { kind: 'idle' }
  | { kind: 'pinging' }
  | {
      kind: 'downloading';
      currentMbps: number;
      samples: number[];
      /** Latency samples taken concurrently with the download. */
      loadedPings: number[];
    }
  | {
      kind: 'uploading';
      currentMbps: number;
      samples: number[];
      loadedPings: number[];
    }
  | {
      kind: 'done';
      pingMs: number;
      downloadMbps: number;
      uploadMbps: number;
      downloadSamples: number[];
      uploadSamples: number[];
      /** Avg ping under download load, ms. null when no loaded samples landed. */
      loadedDownPingMs: number | null;
      /** Avg ping under upload load, ms. null when no loaded samples landed. */
      loadedUpPingMs: number | null;
    }
  | { kind: 'error'; message: string };

/**
 * Built-in speed test — measures ping, download, and upload against
 * Cloudflare's public speed endpoints. Live SVG gauge tracks the
 * current Mbps during each phase; sparkline below replays the curve.
 *
 * The whole flow is sequential (ping → down → up) and runs to
 * completion before re-enabling the Start button. Cancelling
 * mid-test by closing the modal aborts the in-flight fetch via the
 * AbortController.
 */
export function SpeedTestModal({ onClose, onBack }: SpeedTestModalProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [source, setSource] = useState<'cloudflare' | 'edge' | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function runTest(): Promise<void> {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    // Each phase is run inside a labeled try so a failure surfaces
    // *which* leg of the test died — much more actionable than the
    // generic "Failed to fetch" you'd get from a bare catch.
    async function runPhase<T>(label: string, fn: () => Promise<T>): Promise<T> {
      try {
        return await fn();
      } catch (err) {
        if (ac.signal.aborted) throw err;
        const detail = err instanceof Error ? err.message : 'request failed';
        throw new Error(`${label}: ${detail}`);
      }
    }

    try {
      setPhase({ kind: 'pinging' });
      // Pick the byte source first: Cloudflare's CDN edge if reachable,
      // otherwise our own Worker. All three phases use the same source.
      const endpoints = await resolveEndpoints(ac.signal);
      setSource(endpoints.source);
      const pingMs = await runPhase('Ping', () => measurePing(ac.signal, endpoints));

      const downloadSamples: number[] = [];
      const downloadLoadedPings: number[] = [];
      setPhase({
        kind: 'downloading',
        currentMbps: 0,
        samples: downloadSamples,
        loadedPings: downloadLoadedPings,
      });
      const downloadMbps = await runPhase('Download', () =>
        runWithLoadedPings(
          ac.signal,
          endpoints,
          (mbps) => {
            downloadSamples.push(mbps);
            setPhase({
              kind: 'downloading',
              currentMbps: mbps,
              samples: [...downloadSamples],
              loadedPings: [...downloadLoadedPings],
            });
          },
          (loadedMs) => {
            downloadLoadedPings.push(loadedMs);
          },
          (onProgress) => measureDownload(ac.signal, endpoints, onProgress),
        ),
      );

      const uploadSamples: number[] = [];
      const uploadLoadedPings: number[] = [];
      setPhase({
        kind: 'uploading',
        currentMbps: 0,
        samples: uploadSamples,
        loadedPings: uploadLoadedPings,
      });
      const uploadMbps = await runPhase('Upload', () =>
        runWithLoadedPings(
          ac.signal,
          endpoints,
          (mbps) => {
            uploadSamples.push(mbps);
            setPhase({
              kind: 'uploading',
              currentMbps: mbps,
              samples: [...uploadSamples],
              loadedPings: [...uploadLoadedPings],
            });
          },
          (loadedMs) => {
            uploadLoadedPings.push(loadedMs);
          },
          (onProgress) => measureUpload(ac.signal, endpoints, onProgress),
        ),
      );

      setPhase({
        kind: 'done',
        pingMs,
        downloadMbps,
        uploadMbps,
        downloadSamples,
        uploadSamples,
        loadedDownPingMs: avg(downloadLoadedPings),
        loadedUpPingMs: avg(uploadLoadedPings),
      });
    } catch (err) {
      if (ac.signal.aborted) return; // modal closed mid-test
      setPhase({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Speed test failed.',
      });
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
    }
  }

  return (
    <LauncherModal
      title="Speed test"
      onClose={onClose}
      testId="speedtest-modal"
      {...(onBack && { onBack })}
    >
      <div className="speedtest" data-testid="speedtest">
        <BigReadout
          mbps={currentMbps(phase)}
          phase={phaseLabel(phase)}
          accent={phaseAccent(phase)}
        />

        {/* Chart needs at least 1 sample before it can render anything
            useful. While the phase is mid-warm-up (pinging, or
            upload-just-started) show a spinner so the UI doesn't sit
            on an empty rectangle. */}
        {showSpinner(phase) ? (
          <SpeedSpinner accent={phaseAccent(phase)} label={spinnerLabel(phase)} />
        ) : (
          <SpeedChart samples={visibleSamples(phase)} accent={phaseAccent(phase)} />
        )}

        <Summary phase={phase} />

        <div className="speedtest__actions">
          <button
            type="button"
            className="speedtest__start"
            onClick={() => void runTest()}
            disabled={phase.kind === 'pinging' || phase.kind === 'downloading' || phase.kind === 'uploading'}
            data-testid="speedtest-start"
          >
            {phase.kind === 'done' || phase.kind === 'error' ? 'Run again' : 'Start test'}
          </button>
        </div>

        <p className="speedtest__hint">
          Runs {String(DOWNLOAD_STREAMS)} parallel streams for
          ~{String(Math.round(MEASURE_MS / 1000))}s each way, ignoring the
          first {String(WARMUP_MS / 1000)}s of ramp-up
          {source === 'cloudflare'
            ? ', via Cloudflare’s global speed edge'
            : source === 'edge'
              ? ', via MobiLauncher’s edge (Cloudflare host unreachable)'
              : ''}
          . Saturates the link like fast.com, so it uses real data —
          avoid on a metered connection.
        </p>
      </div>
    </LauncherModal>
  );
}

/* ───── Phase helpers ───── */

function currentMbps(phase: Phase): number {
  if (phase.kind === 'downloading') return phase.currentMbps;
  if (phase.kind === 'uploading') return phase.currentMbps;
  if (phase.kind === 'done') return phase.downloadMbps;
  return 0;
}

function phaseLabel(phase: Phase): string {
  switch (phase.kind) {
    case 'idle':        return 'Idle';
    case 'pinging':     return 'Pinging…';
    case 'downloading': return 'Download';
    case 'uploading':   return 'Upload';
    case 'done':        return 'Done';
    case 'error':       return 'Error';
  }
}

function phaseAccent(phase: Phase): string {
  if (phase.kind === 'uploading') return '#9b59ff'; // purple for up
  if (phase.kind === 'error') return '#ef4444';
  return '#1976d2'; // brand blue for everything else
}

function visibleSamples(phase: Phase): number[] {
  if (phase.kind === 'downloading' || phase.kind === 'uploading') return phase.samples;
  if (phase.kind === 'done') return phase.downloadSamples;
  return [];
}

function showSpinner(phase: Phase): boolean {
  if (phase.kind === 'pinging') return true;
  // The split-second between "uploading" starting and its first chunk
  // landing — keeps the layout from flickering through an empty chart.
  if (phase.kind === 'uploading' && phase.samples.length === 0) return true;
  return false;
}

function spinnerLabel(phase: Phase): string {
  if (phase.kind === 'pinging') return 'Measuring latency…';
  if (phase.kind === 'uploading') return 'Warming up the upload…';
  return '';
}

/* ───── Big-number readout (Fast.com style) ─────────────────────────── */

interface ReadoutProps {
  mbps: number;
  phase: string;
  accent: string;
}

function BigReadout({ mbps, phase, accent }: ReadoutProps): JSX.Element {
  return (
    <div className="speedtest__readout-big">
      <div className="speedtest__phase" style={{ color: accent }}>
        {phase}
      </div>
      <div className="speedtest__value-row">
        <span className="speedtest__value" style={{ color: accent }}>
          {formatMbps(mbps)}
        </span>
        <span className="speedtest__unit">Mbps</span>
      </div>
    </div>
  );
}

/* ───── Live area chart ───────────────────────────────────────────────
 * Pure-SVG area + line. Re-renders on each sample (~every 100ms during
 * download); the area sweeps left-to-right as samples accrue. No
 * animation tweens — the visual motion is the chart growing.
 *
 * Layout: 360×120 viewBox, 24px reserved on the left for the y-axis
 * peak label so the chart curve doesn't collide with it. Three soft
 * horizontal gridlines (0 / mid / peak) anchor the eye; the area gets
 * a top-down gradient fill in the phase accent color.
 */
function SpeedChart({ samples, accent }: { samples: number[]; accent: string }): JSX.Element {
  const W = 360;
  const H = 120;
  const padL = 28;
  const padR = 8;
  const padT = 8;
  const padB = 16;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  // Round the chart's top-of-scale to a nice number so the peak label
  // doesn't read e.g. "172.4". A clamped max of 10 keeps the y-axis
  // from collapsing on a flat-zero chart (first frame of a test).
  const rawPeak = samples.length > 0 ? Math.max(...samples) : 0;
  const peak = niceCeil(Math.max(rawPeak, 10));
  const mid = peak / 2;
  // Gradient id is randomized so two charts on the same page (a future
  // contingency) don't fight over the same defs entry.
  const gradId = `speedtest-grad-${accent.replace(/[^a-z0-9]/gi, '')}`;

  function xAt(i: number, n: number): number {
    if (n <= 1) return padL;
    return padL + (i / (n - 1)) * chartW;
  }
  function yAt(v: number): number {
    if (peak <= 0) return padT + chartH;
    return padT + chartH - (v / peak) * chartH;
  }

  const points =
    samples.length === 0
      ? ''
      : samples.map((v, i) => `${xAt(i, samples.length).toFixed(2)},${yAt(v).toFixed(2)}`).join(' ');
  // Area polygon: chart-baseline → curve → back to baseline. Closed shape so we can fill it.
  const lastX = samples.length > 0 ? xAt(samples.length - 1, samples.length) : padL;
  const areaPoints =
    samples.length === 0
      ? ''
      : `${padL.toFixed(2)},${(padT + chartH).toFixed(2)} ${points} ${lastX.toFixed(2)},${(padT + chartH).toFixed(2)}`;

  return (
    <div className="speedtest__chart" aria-hidden>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.45" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Gridlines + Y-axis labels for 0 / mid / peak. */}
        {[
          { v: peak, label: formatMbps(peak) },
          { v: mid, label: formatMbps(mid) },
          { v: 0, label: '0' },
        ].map((row) => (
          <g key={row.v}>
            <line
              x1={padL}
              y1={yAt(row.v)}
              x2={W - padR}
              y2={yAt(row.v)}
              stroke="rgba(255, 255, 255, 0.06)"
              strokeWidth="1"
            />
            <text
              x={padL - 6}
              y={yAt(row.v) + 4}
              textAnchor="end"
              fontSize="10"
              fill="rgba(255, 255, 255, 0.45)"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {row.label}
            </text>
          </g>
        ))}

        {/* Area fill */}
        {areaPoints !== '' && <polygon points={areaPoints} fill={`url(#${gradId})`} />}

        {/* Curve */}
        {points !== '' && (
          <polyline
            points={points}
            fill="none"
            stroke={accent}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Current-value marker — small filled circle on the last point. */}
        {samples.length > 0 && (
          <circle
            cx={xAt(samples.length - 1, samples.length)}
            cy={yAt(samples[samples.length - 1] ?? 0)}
            r="4"
            fill={accent}
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth="2"
          />
        )}
      </svg>
    </div>
  );
}

/* ───── Spinner — shown when the chart has no samples yet ─────────── */

function SpeedSpinner({ accent, label }: { accent: string; label: string }): JSX.Element {
  return (
    <div className="speedtest__spinner-wrap" role="status" aria-live="polite">
      <span
        className="speedtest__spinner"
        style={{ borderTopColor: accent }}
        aria-hidden
      />
      <span className="speedtest__spinner-label">{label}</span>
    </div>
  );
}

/** Round up to a nice display number (10, 25, 50, 100, 200, 500, 1000…). */
function niceCeil(v: number): number {
  if (v <= 10) return 10;
  if (v <= 25) return 25;
  if (v <= 50) return 50;
  if (v <= 100) return 100;
  if (v <= 200) return 200;
  if (v <= 500) return 500;
  if (v <= 1000) return 1000;
  return Math.ceil(v / 100) * 100;
}

/* ───── Summary ───── */

function Summary({ phase }: { phase: Phase }): JSX.Element {
  if (phase.kind === 'idle') {
    return (
      <p className="speedtest__placeholder">
        Tap <strong>Start test</strong> to measure download, upload, and ping.
      </p>
    );
  }
  if (phase.kind === 'error') {
    return (
      <p className="speedtest__placeholder speedtest__placeholder--err" role="alert">
        {phase.message}
      </p>
    );
  }
  const pingMs = phase.kind === 'done' ? phase.pingMs : null;
  const downMbps = phase.kind === 'done' ? phase.downloadMbps : null;
  const upMbps = phase.kind === 'done' ? phase.uploadMbps : null;
  const loadedDownMs = phase.kind === 'done' ? phase.loadedDownPingMs : null;
  const loadedUpMs = phase.kind === 'done' ? phase.loadedUpPingMs : null;
  // Worst-of-the-two loaded pings is the bufferbloat-relevant number —
  // operators care most about "how bad does my latency get when the
  // pipe is busy?" not which direction made it bad.
  const worstLoaded =
    loadedDownMs !== null && loadedUpMs !== null
      ? Math.max(loadedDownMs, loadedUpMs)
      : (loadedDownMs ?? loadedUpMs);

  // Bufferbloat = loaded − unloaded latency. Positive numbers mean the
  // network buffers a lot under load (bad for video calls, gaming);
  // values under ~30ms are typical of well-tuned consumer ISPs.
  const bufferbloatMs =
    pingMs !== null && worstLoaded !== null ? Math.max(0, worstLoaded - pingMs) : null;

  return (
    <div className="speedtest__summary">
      <SummaryCell label="Download" value={formatMbps(downMbps)} unit="Mbps" />
      <SummaryCell label="Upload" value={formatMbps(upMbps)} unit="Mbps" />
      <SummaryCell
        label="Latency"
        value={pingMs === null ? '—' : Math.round(pingMs).toString()}
        unit="ms"
        {...(worstLoaded !== null && {
          sub: `${Math.round(worstLoaded).toString()} under load`,
        })}
      />
      <SummaryCell
        label="Bufferbloat"
        value={bufferbloatMs === null ? '—' : `+${Math.round(bufferbloatMs).toString()}`}
        unit="ms"
        {...(() => {
          const rating = bufferbloatRating(bufferbloatMs);
          return rating !== undefined ? { sub: rating } : {};
        })()}
      />
    </div>
  );
}

function bufferbloatRating(deltaMs: number | null): string | undefined {
  if (deltaMs === null) return undefined;
  if (deltaMs < 30) return 'good';
  if (deltaMs < 100) return 'fair';
  return 'high';
}

function SummaryCell({
  label,
  value,
  unit,
  sub,
}: {
  label: string;
  value: string;
  unit: string;
  /** Optional sub-line below the unit — e.g. "125 under load", "good". */
  sub?: string;
}): JSX.Element {
  return (
    <div className="speedtest__summary-cell">
      <span className="speedtest__summary-label">{label}</span>
      <span className="speedtest__summary-value">{value}</span>
      <span className="speedtest__summary-unit">{unit}</span>
      {sub !== undefined && <span className="speedtest__summary-sub">{sub}</span>}
    </div>
  );
}

function formatMbps(v: number | null): string {
  if (v === null) return '—';
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

/* ───── Measurement primitives ──────────────────────────────────────── */

/**
 * Probe Cloudflare's speed host once with a short timeout. Reachable →
 * use it (CDN line-rate). Blocked / timed out → fall back to our edge
 * Worker so the test still runs behind a locked-down network.
 */
async function resolveEndpoints(signal: AbortSignal): Promise<SpeedEndpoints> {
  const ctrl = new AbortController();
  const relay = (): void => ctrl.abort();
  if (signal.aborted) ctrl.abort();
  else signal.addEventListener('abort', relay, { once: true });
  const timer = setTimeout(() => ctrl.abort(), CF_PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(CLOUDFLARE_ENDPOINTS.pingUrl(), {
      method: 'GET',
      cache: 'no-store',
      signal: ctrl.signal,
    });
    await res.arrayBuffer();
    if (res.ok) return CLOUDFLARE_ENDPOINTS;
  } catch {
    // unreachable / blocked / timed out — fall through to the edge.
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', relay);
  }
  return EDGE_ENDPOINTS;
}

async function measurePing(signal: AbortSignal, endpoints: SpeedEndpoints): Promise<number> {
  const samples: number[] = [];
  for (let i = 0; i < PING_SAMPLES; i++) {
    const start = performance.now();
    const res = await fetch(endpoints.pingUrl(), {
      method: 'GET',
      cache: 'no-store',
      signal,
    });
    // Drain the (empty) response so the round-trip time we measure
    // is the full transaction, not just first-byte.
    await res.arrayBuffer();
    samples.push(performance.now() - start);
  }
  // Discard the first sample (warm-up / DNS / TLS) and average the rest.
  const useful = samples.slice(1);
  return useful.reduce((a, b) => a + b, 0) / useful.length;
}

async function measureDownload(
  signal: AbortSignal,
  endpoints: SpeedEndpoints,
  onProgress: (mbps: number) => void,
): Promise<number> {
  return runThroughputPhase(
    signal,
    onProgress,
    (s, onBytes) => drainDownloadStream(s, endpoints, onBytes),
    DOWNLOAD_STREAMS,
  );
}

async function measureUpload(
  signal: AbortSignal,
  endpoints: SpeedEndpoints,
  onProgress: (mbps: number) => void,
): Promise<number> {
  return runThroughputPhase(
    signal,
    onProgress,
    (s, onBytes) => uploadLoop(s, endpoints, onBytes),
    UPLOAD_STREAMS,
  );
}

/**
 * Time-boxed, warm-up-excluded throughput core shared by download and
 * upload. Spawns `workerCount` concurrent transfer loops that each add
 * to a shared byte counter; samples aggregate Mbps every SAMPLE_MS;
 * discards the first WARMUP_MS; returns steady-state Mbps over the
 * remaining window. Ends at MEASURE_MS, at MAX_PHASE_BYTES, or when the
 * caller's signal aborts (modal closed).
 */
async function runThroughputPhase(
  outer: AbortSignal,
  onProgress: (mbps: number) => void,
  spawnWorker: (signal: AbortSignal, onBytes: (n: number) => void) => Promise<void>,
  workerCount: number,
): Promise<number> {
  // Inner controller so we can stop the workers when the window closes
  // without aborting the caller's signal. Mirror the caller's abort.
  const ac = new AbortController();
  const relay = (): void => ac.abort();
  if (outer.aborted) ac.abort();
  else outer.addEventListener('abort', relay, { once: true });

  let total = 0;
  const onBytes = (n: number): void => {
    total += n;
    if (total >= MAX_PHASE_BYTES) ac.abort();
  };
  const workers = Array.from({ length: workerCount }, () => spawnWorker(ac.signal, onBytes));

  const t0 = performance.now();
  let warmEnd = 0;
  let warmBytes = 0;
  try {
    for (;;) {
      await sleep(SAMPLE_MS);
      const now = performance.now();
      const elapsed = now - t0;
      if (warmEnd === 0 && elapsed >= WARMUP_MS) {
        warmEnd = now;
        warmBytes = total;
      }
      // Keep the gauge moving the whole time: live cumulative Mbps
      // during warm-up (so the chart shows the ramp climbing), then
      // steady-state once warm-up ends. Only the steady-state figure
      // feeds the final result below.
      if (warmEnd === 0) {
        const secs = elapsed / 1000;
        if (secs > 0) onProgress(toMbps(total, secs));
      } else {
        const secs = (now - warmEnd) / 1000;
        if (secs > 0) onProgress(toMbps(total - warmBytes, secs));
      }
      if (elapsed >= MEASURE_MS || ac.signal.aborted) break;
    }
  } finally {
    ac.abort();
    outer.removeEventListener('abort', relay);
    // Workers reject with AbortError once we abort — swallow them.
    await Promise.allSettled(workers);
  }

  if (warmEnd === 0) {
    // Phase ended before warm-up completed (modal closed early) — fall
    // back to a whole-window average so we still return a sane number.
    return toMbps(total, Math.max(0.001, (performance.now() - t0) / 1000));
  }
  return toMbps(total - warmBytes, Math.max(0.001, (performance.now() - warmEnd) / 1000));
}

/**
 * One download stream. Loops until aborted: against Cloudflare each
 * `__down?bytes=N` request ends after N bytes, so we re-request to stay
 * busy for the whole window; against our edge the `?stream=1` body is
 * unbounded, so the inner read loop only ends on abort and the outer
 * loop runs exactly once.
 */
async function drainDownloadStream(
  signal: AbortSignal,
  endpoints: SpeedEndpoints,
  onBytes: (n: number) => void,
): Promise<void> {
  while (!signal.aborted) {
    const res = await fetch(endpoints.downloadUrl(), {
      method: 'GET',
      cache: 'no-store',
      signal,
    });
    if (!res.body) {
      const buf = await res.arrayBuffer();
      onBytes(buf.byteLength);
      continue;
    }
    const reader = res.body.getReader();
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      if (value) onBytes(value.byteLength);
    }
  }
}

/* One reusable upload payload, generated once at module load. Reads are
 * non-mutating so the same buffer is safe to POST from every concurrent
 * upload loop. */
const UPLOAD_CHUNK = (() => {
  const b = new Uint8Array(UPLOAD_CHUNK_BYTES);
  for (let i = 0; i < b.length; i++) b[i] = (i * 31) & 0xff;
  return b;
})();

/** One upload stream: POST the chunk in a loop until aborted, counting
 *  bytes only once each POST's round-trip completes.
 *
 *  No `Content-Type` header on purpose: a cross-origin POST with
 *  octet-stream would trigger a CORS preflight, and Cloudflare's __up
 *  doesn't answer OPTIONS cleanly. Omitting it keeps the request
 *  "simple" (no preflight). fetch still sets Content-Length from the
 *  BufferSource, which our edge fallback reads. */
async function uploadLoop(
  signal: AbortSignal,
  endpoints: SpeedEndpoints,
  onBytes: (n: number) => void,
): Promise<void> {
  while (!signal.aborted) {
    const res = await fetch(endpoints.uploadUrl, {
      method: 'POST',
      body: UPLOAD_CHUNK,
      signal,
    });
    await res.arrayBuffer();
    if (!signal.aborted) onBytes(UPLOAD_CHUNK_BYTES);
  }
}

/**
 * Wraps a phase's body in a concurrent latency-probe loop: while the
 * download or upload is running we fire a small ping every ~500ms
 * and forward each round-trip time to `onLoadedPing`. The result is
 * an "under load" latency series that captures bufferbloat — the
 * extent to which a busy uplink/downlink starves small interactive
 * packets. Difference vs the unloaded ping = bufferbloat score.
 *
 * The loop stops as soon as the phase's body resolves (success OR
 * failure), so a failed download doesn't leave a stray ping loop
 * running.
 */
async function runWithLoadedPings<T>(
  signal: AbortSignal,
  endpoints: SpeedEndpoints,
  onProgress: (mbps: number) => void,
  onLoadedPing: (ms: number) => void,
  body: (onProgress: (mbps: number) => void) => Promise<T>,
): Promise<T> {
  let running = true;
  const result = body(onProgress).finally(() => {
    running = false;
  });

  void (async () => {
    // 350ms first delay gives the bulk transfer a moment to ramp up
    // before we start probing; otherwise the first "loaded" ping is
    // really an unloaded one.
    await sleep(350);
    while (running && !signal.aborted) {
      try {
        const ms = await singlePing(signal, endpoints);
        if (running && !signal.aborted) onLoadedPing(ms);
      } catch {
        // Ignore individual ping failures — they're not load-bearing.
      }
      await sleep(500);
    }
  })();

  return result;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function singlePing(signal: AbortSignal, endpoints: SpeedEndpoints): Promise<number> {
  const start = performance.now();
  const res = await fetch(endpoints.pingUrl(), {
    method: 'GET',
    cache: 'no-store',
    signal,
  });
  await res.arrayBuffer();
  return performance.now() - start;
}

function avg(samples: number[]): number | null {
  if (samples.length === 0) return null;
  return samples.reduce((a, b) => a + b, 0) / samples.length;
}

function toMbps(bytes: number, seconds: number): number {
  if (seconds <= 0) return 0;
  return (bytes * 8) / (seconds * 1_000_000);
}

